// pages/master-data/Route/MasterRouteAddEdit.jsx
'use client';

import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, TextField, CircularProgress, Button, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import RoutePolygonTable from './RoutePolygonTable';
import VehicleAssignTable from './VehicleAssignTable';
import { useMasterVehicleList } from '../../../stores/masterVehicleStore';
// API Stores Integration
import { createRouteTrip, updateRouteTripAll, getRouteTripById } from '../../../stores/routeTripStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import PickerField from '../../../components/PickerField';
import usePickerPagination from '../../../hooks/usePickerPagination';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';
import InfoIcon from '@mui/icons-material/Info';

const MasterRouteAddEdit = forwardRef(({ open, id = null, onClose = null, onSuccess = null, onError = null, tabMode = 'active' }, ref) => {
  const { t } = useLanguage();
  const [initialValues, setInitialValues] = useState({
    name: '',
    company: null,
    polygons: [],
    vehicles: []
  });

  const [loading, setLoading] = useState(false);
  const notify = useNotifier();
  const formikRef = useRef(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const [vehicleSearchText, setVehicleSearchText] = useState('');
  const [vehiclePage, setVehiclePage] = useState(0);
  const [debouncedVehicleSearch, setDebouncedVehicleSearch] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedVehicleSearch(vehicleSearchText);
      setVehiclePage(0);
    }, 500);
    return () => clearTimeout(handler);
  }, [vehicleSearchText]);

  // Company Pagination Hook
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  // Removed redundant Auto-fill Company from Local Storage useEffect to avoid conflicting with fetchRouteData

  // Fetch Vehicles for VehicleAssignTable
  const { data: fetchedVehicleList = [], isLoading: loadingVehicles } = useMasterVehicleList(
    vehiclePage,
    100,
    selectedCompanyId ? [{ value: selectedCompanyId }] : [],
    'license_plate',
    debouncedVehicleSearch
  );

  const [vehicleList, setVehicleList] = useState([]);

  useEffect(() => {
    if (vehiclePage === 0) {
      setVehicleList(fetchedVehicleList);
    } else {
      setVehicleList((prev) => {
        const existingIds = new Set(prev.map((v) => v.id || v.vehicle_id));
        const newItems = fetchedVehicleList.filter((v) => !existingIds.has(v.id || v.vehicle_id));
        return [...prev, ...newItems];
      });
    }
  }, [fetchedVehicleList, vehiclePage]);

  const handleLoadMoreVehicles = () => {
    if (!loadingVehicles && fetchedVehicleList.length === 20) {
      setVehiclePage((prev) => prev + 1);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => formikRef.current?.submitForm?.()
  }));

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchRouteData = async () => {
      if (!id) {
        let storedCompanyId = null;
        try {
          storedCompanyId = localStorage.getItem('companyId');
        } catch {}

        setInitialValues({
          name: '',
          company: storedCompanyId ? String(storedCompanyId) : null,
          effective_date: new Date().toISOString().slice(0, 10),
          polygons: [{ id: Date.now(), lokasi: null }], // Start with one empty row
          vehicles: []
        });

        if (storedCompanyId) {
          setSelectedCompanyId(String(storedCompanyId));
        }

        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getRouteTripById(id);

        if (!mounted) return;
        if (data) {
          const uiPolygons = (data.spatials || []).map((p, idx) => ({
            id: idx,
            lokasi: {
              label: p.spatial_name,
              type: p.spatial_type,
              realId: p.spatial_id
            }
          }));

          const uiVehicles = (data.vehicles || []).map((v) => {
            let vehicleObj = null;

            if (typeof v.vehicle === 'object' && v.vehicle !== null) {
              vehicleObj = v.vehicle;
            } else if (v.join) {
              vehicleObj = {
                id: v.vehicle,
                vehicle_id: v.vehicle,
                ...v.join
              };
            } else {
              vehicleObj = { id: v.vehicle };
            }

            return {
              vehicle: vehicleObj,
              assign_date: v.valid_date || data.valid_date || dayjs().format('YYYY-MM-DD')
            };
          });

          const cId = data.company_id || data.company;
          setInitialValues({
            name: data.name ?? '',
            company: cId ? String(cId) : null,
            vehicles: uiVehicles,
            polygons: uiPolygons.length > 0 ? uiPolygons : [{ id: Date.now(), lokasi: null }]
          });
          if (cId) {
            setSelectedCompanyId(String(cId));
          }
        }
      } catch (err) {
        console.error('Failed to load Route Trip:', err);
        notify({ message: t('masterRoute.failedLoadRoute', 'Failed to load Route data'), severity: 'error' });
        if (onError) onError();
        if (onClose) onClose();
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchRouteData();

    return () => {
      mounted = false;
    };
  }, [id, open, notify, onError, onClose, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('masterRoute.routeNameRequired', 'Route Name is required')),
    company: Yup.mixed().required(t('masterRoute.companyRequired', 'Company must be selected')),
    polygons: Yup.array()
      .min(2, t('masterRoute.minPolygonRequired', 'Must select at least 2 locations (Start & End)'))
      .test(
        'valid-polygons',
        t('masterRoute.allPolygonMustBeFilled', 'All location rows must be filled'),
        (val) => val && val.every((item) => item.lokasi !== null)
      ),
    vehicles: Yup.array().test(
      'valid-vehicles',
      t('masterRoute.selectVehicleForAddedRow', 'Please select a vehicle for the added row'),
      (val) => !val || val.every((item) => item.vehicle !== null)
    )
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const buildPayload = (vals) => ({
        name: formatUpperCase(vals.name),
        company: Number(vals.company),
        spatials: (vals.polygons || []).map((p, index) => ({
          spatial_id: p.lokasi?.realId,
          spatial_name: p.lokasi?.label,
          spatial_type: p.lokasi?.type,
          sequence_number: index + 1
        })),
        vehicles: (vals.vehicles || []).map((v) => ({
          vehicle: v.vehicle?.id || v.vehicle?.vehicle_id || v.vehicle,
          valid_date: dayjs(v.assign_date).startOf('day').format()
        }))
      });

      const payload = buildPayload(values);
      if (tabMode === 'history') {
        payload.is_active = false;
      }

      let response;

      if (id) {
        const initialPayload = buildPayload(formikRef.current?.initialValues || initialValues);
        const isDirty = JSON.stringify(payload) !== JSON.stringify(initialPayload);

        if (!isDirty) {
          notify({ message: t('notify.noChanges', 'No changes made'), severity: 'info' });
          if (onClose) onClose();
          setSubmitting(false);
          return;
        }

        response = await updateRouteTripAll(id, payload);
      } else {
        response = await createRouteTrip(payload);
      }

      if (response?.code === 400 || response?.status === false || response?.error) {
        throw response;
      }

      notify({
        message: id ? t('notify.updateSuccess', 'Update Successfully') : t('notify.createSuccess', 'Created Successfully'),
        severity: 'success'
      });
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage =
        err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
      notify({ message: errorMessage, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(formik) => {
          const { values, handleBlur, setFieldValue, touched, errors, isSubmitting } = formik;
          return (
            <FormDialog
              open={open}
              onClose={onClose}
              title={
                <Typography variant="h6">
                  {id ? t('masterRoute.editRouteTrip', 'Edit Route Trip') : t('masterRoute.addRouteTrip', 'Add Route Trip')}
                </Typography>
              }
              fullScreenMode={true}
              fullHeight={false}
              actions={
                <>
                  <Button onClick={onClose} disabled={isSubmitting} variant="outlined">
                    {t('general.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    form="master-route-form"
                    disabled={isSubmitting || loading}
                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{
                      backgroundColor: '#0072DA',
                      textTransform: 'none',
                      borderRadius: 1,
                      px: 3,
                      fontWeight: 700,
                      boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
                    }}
                  >
                    {isSubmitting ? t('general.processing', 'Processing...') : t('general.save', 'Save')}
                  </Button>
                </>
              }
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ width: '100%', maxWidth: 'none' }}>
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: tabMode === 'active' ? '#e3f2fd' : '#fff3e0',
                      borderRadius: 2,
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start'
                    }}
                  >
                    <InfoIcon sx={{ color: tabMode === 'active' ? '#1976d2' : '#ed6c02', mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: tabMode === 'active' ? '#1976d2' : '#ed6c02' }}>
                        {tabMode === 'active' ? 'Active Route Mode' : 'Historical Route Mode'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {tabMode === 'active'
                          ? 'In Active Route mode, assigned vehicles must have an assignment date of today or in the future.'
                          : 'In Historical Route mode, assigned vehicles must have an assignment date in the past (before today). This route will be saved as inactive.'}
                      </Typography>
                    </Box>
                  </Box>
                  <form id="master-route-form" onSubmit={formik.handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
                      {/* KOLOM KIRI: INFO DASAR & VEHICLE ASSIGN */}
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fbfbfb' }}>
                          <Typography variant="h4" fontWeight="bold" color="#485D9A" mb={2}>
                            {t('masterRoute.mainInformation', 'Main Information')}
                          </Typography>
                          <Stack>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#334155' }}>
                                {t('masterCompany.company', 'Company')} <span style={{ color: 'red' }}>*</span>
                              </Typography>
                              <PickerField
                                name="company"
                                options={companyPickerParams.options}
                                value={values.company}
                                onChange={(val) => {
                                  const selectedId = typeof val === 'object' && val !== null ? val.value : val;
                                  setFieldValue('company', selectedId);
                                  setSelectedCompanyId(String(selectedId || ''));
                                  try {
                                    if (selectedId) localStorage.setItem('companyId', String(selectedId));
                                  } catch {}
                                }}
                                onBlur={handleBlur}
                                error={touched.company && Boolean(errors.company)}
                                helperText={touched.company && errors.company}
                                placeholder={t('masterRoute.selectCompany', 'Select Company')}
                                {...companyPickerParams}
                                disabled={isSubmitting}
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#334155' }}>
                                {t('masterRoute.routeName', 'Route Name')} <span style={{ color: 'red' }}>*</span>
                              </Typography>
                              <TextField
                                fullWidth
                                size="small"
                                id="name"
                                name="name"
                                placeholder={t('masterRoute.inputRouteName', 'Enter route name')}
                                value={values.name}
                                onChange={(e) => {
                                  setFieldValue('name', formatUpperCase(e.target.value));
                                }}
                                onBlur={handleBlur}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                disabled={isSubmitting}
                              />
                            </Box>
                          </Stack>
                        </Box>

                        {/* Assign Vehicle Section using VehicleAssignTable */}
                        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, position: 'relative' }}>
                          {!values.company && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(255,255,255,0.7)',
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2
                              }}
                            >
                              <Typography color="error" fontWeight="bold">
                                {t('general.selectCompanyFirst', '* Please select a company first')}
                              </Typography>
                            </Box>
                          )}
                          <VehicleAssignTable
                            assignedVehicles={values.vehicles}
                            setAssignedVehicles={(newVehicles) => setFieldValue('vehicles', newVehicles)}
                            availableVehicles={vehicleList}
                            isLoadingVehicles={loadingVehicles}
                            onSearchVehicle={setVehicleSearchText}
                            onLoadMoreVehicles={handleLoadMoreVehicles}
                            isReadOnly={false}
                            tabMode={tabMode}
                            isEditMode={!!id}
                          />
                          {typeof errors.vehicles === 'string' && (
                            <Typography color="error" variant="caption" sx={{ mt: 1, fontWeight: 'bold', display: 'block' }}>
                              * {errors.vehicles}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* KOLOM KANAN: POLYGON TABLE */}
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                        {!values.company && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'rgba(255,255,255,0.7)',
                              zIndex: 10,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography color="error" fontWeight="bold">
                              {t('general.selectCompanyFirst', '* Please select a company first')}
                            </Typography>
                          </Box>
                        )}
                        <RoutePolygonTable
                          routes={values.polygons}
                          setRoutes={(newPolygons) => setFieldValue('polygons', newPolygons)}
                          companyId={selectedCompanyId}
                        />
                        {typeof errors.polygons === 'string' && (
                          <Typography color="error" variant="caption" sx={{ mt: 1, fontWeight: 'bold' }}>
                            * {errors.polygons}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </form>
                </Box>
              )}
            </FormDialog>
          );
        }}
      </Formik>
    </LocalizationProvider>
  );
});

export default MasterRouteAddEdit;
